/**
 * TASKS-V2 E2 — grupo temporal colapsável.
 *
 * Renderiza header clicável (dot, label, count, chevron) + lista
 * de children (TaskCard via render prop ou children directly).
 * Estado de colapso persistido em localStorage por group_id.
 *
 * Acessibilidade:
 *   - Header é <button> (Tab navega, Enter/Space toggle)
 *   - aria-expanded refletindo estado
 *   - aria-controls aponta pro id da lista
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { GROUP_LABELS, GROUP_COLORS } from "@/utils/taskGrouping";
import trace from "@/services/executionTrace";

const STORAGE_KEY_PREFIX = "nexo_tasks_group_collapsed_";

function _readPersisted(groupId, defaultCollapsed) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + groupId);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch {/* private mode */}
  return defaultCollapsed;
}

function _writePersisted(groupId, collapsed) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + groupId, String(collapsed));
  } catch {/* */}
}

export default function TaskGroup({
  groupId,
  count,
  defaultCollapsed = false,
  children,
}) {
  const label = GROUP_LABELS[groupId] || groupId;
  const colors = GROUP_COLORS[groupId] || GROUP_COLORS.future;

  const [collapsed, setCollapsed] = useState(() =>
    _readPersisted(groupId, defaultCollapsed),
  );

  // Persiste sempre que o estado muda (e re-aplica defaultCollapsed
  // se localStorage não tinha valor — primeira render).
  useEffect(() => {
    _writePersisted(groupId, collapsed);
  }, [groupId, collapsed]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        trace.record("tasks.group.toggle", {
          group: groupId,
          expanded: !next,
        });
      } catch {/* */}
      return next;
    });
  }, [groupId]);

  const listId = `task-group-${groupId}`;

  return (
    <div className="mb-3" data-testid={`task-group-${groupId}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-controls={listId}
        className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/[0.02] transition-colors text-left"
      >
        <span
          className={`inline-block w-2 h-2 rounded-full ${colors.dot}`}
          aria-hidden="true"
        />
        <span className={`text-sm font-medium ${colors.text}`}>{label}</span>
        <span className={`text-xs ${colors.count}`}>({count})</span>
        <ChevronRight
          className={`w-4 h-4 text-slate-500 ml-auto transition-transform ${
            collapsed ? "" : "rotate-90"
          }`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            id={listId}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div
              className="glass-card rounded-2xl divide-y divide-white/[0.03] mt-1"
              data-testid={`task-group-list-${groupId}`}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
